import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../lib/supabase';

const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const birth = new Date(`${dateOfBirth}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age;
};

const PlayerAgeOverrides = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState('');

  const loadPlayers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('team_members_aox1234')
      .select('id,name,date_of_birth,age,age_override,team_categories,is_active,is_published,sort_order')
      .eq('is_staff', false)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      setMessage(`Σφάλμα: ${error.message}`);
      setLoading(false);
      return;
    }

    const rows = data || [];
    setPlayers(rows);
    setValues(Object.fromEntries(rows.map((p) => [p.id, p.age_override ?? ''])));
    setLoading(false);
  };

  useEffect(() => {
    loadPlayers();
  }, []);

  const rows = useMemo(
    () => players.map((player) => ({ ...player, calculatedAge: calculateAge(player.date_of_birth) })),
    [players]
  );

  const saveOverride = async (player) => {
    setSavingId(player.id);
    setMessage('');

    const raw = values[player.id];
    const parsed = raw === '' || raw === null || raw === undefined ? null : Number(raw);
    if (parsed !== null && (!Number.isInteger(parsed) || parsed < 0 || parsed > 100)) {
      setMessage('Η προσαρμοσμένη ηλικία πρέπει να είναι ακέραιος από 0 έως 100.');
      setSavingId(null);
      return;
    }

    const { error } = await supabase
      .from('team_members_aox1234')
      .update({ age_override: parsed, updated_at: new Date().toISOString() })
      .eq('id', player.id);

    if (error) {
      setMessage(`Σφάλμα: ${error.message}`);
    } else {
      setMessage(parsed === null ? `Η ${player.name} επέστρεψε στην αυτόματη ηλικία.` : `Η ηλικία της ${player.name} ορίστηκε σε ${parsed}.`);
      await loadPlayers();
    }
    setSavingId(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-black text-gray-900">Ηλικίες Παικτριών</h1>
            <p className="text-gray-600 mt-1">
              Η ηλικία υπολογίζεται αυτόματα από την ημερομηνία γέννησης. Συμπληρώστε override μόνο όταν θέλετε να εμφανίζεται διαφορετική ηλικία.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => navigate('/admin/team')} className="px-4 py-2 rounded-lg border bg-white font-semibold">Επιστροφή στην Ομάδα</button>
            <button onClick={loadPlayers} className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold">Ανανέωση</button>
          </div>
        </div>

        {message && <div className="mb-4 rounded-lg border bg-white p-4 text-sm font-medium">{message}</div>}

        <div className="bg-white rounded-xl shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">Φόρτωση...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px]">
                <thead className="bg-gray-50 text-left text-sm text-gray-600">
                  <tr>
                    <th className="px-4 py-3">Παίκτρια</th>
                    <th className="px-4 py-3">Ημ. γέννησης</th>
                    <th className="px-4 py-3">Αυτόματη ηλικία</th>
                    <th className="px-4 py-3">Custom ηλικία</th>
                    <th className="px-4 py-3">Θα εμφανίζεται</th>
                    <th className="px-4 py-3">Ενέργεια</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((player) => {
                    const overrideRaw = values[player.id];
                    const hasOverride = overrideRaw !== '' && overrideRaw !== null && overrideRaw !== undefined;
                    const displayedAge = hasOverride ? overrideRaw : (player.calculatedAge ?? player.age ?? '—');
                    return (
                      <tr key={player.id} className={!player.is_active ? 'opacity-60' : ''}>
                        <td className="px-4 py-3">
                          <div className="font-bold text-gray-900">{player.name}</div>
                          <div className="text-xs text-gray-500">{(player.team_categories || []).join(', ') || '—'}</div>
                        </td>
                        <td className="px-4 py-3 text-sm">{player.date_of_birth || '—'}</td>
                        <td className="px-4 py-3 font-semibold">{player.calculatedAge ?? '—'}</td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={overrideRaw}
                            onChange={(e) => setValues((prev) => ({ ...prev, [player.id]: e.target.value }))}
                            placeholder="Αυτόματο"
                            className="w-28 border rounded-lg px-3 py-2"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-3 py-1 text-sm font-black ${hasOverride ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                            {displayedAge}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveOverride(player)}
                              disabled={savingId === player.id}
                              className="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-bold disabled:opacity-50"
                            >
                              {savingId === player.id ? 'Αποθήκευση...' : 'Αποθήκευση'}
                            </button>
                            <button
                              onClick={() => setValues((prev) => ({ ...prev, [player.id]: '' }))}
                              className="px-3 py-2 rounded-lg border text-sm font-bold"
                              title="Καθαρίστε το πεδίο και πατήστε Αποθήκευση για επιστροφή στην αυτόματη ηλικία"
                            >
                              Αυτόματο
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerAgeOverrides;
